// =============================================================================
// TEST DATA GENERATOR
// Generates test borefields with wells and reports for development/testing
// =============================================================================

import type {
  Borefield,
  Bore,
  SurveyPoint,
  HoleSection,
} from "../features/borefields/types";
import type { Report } from "../features/reports/types";
import type { Rig } from "../features/rigs/types";
import type {
  TubularProgram,
  TubularString,
  TubularStringType,
  CementInterval,
  TallyJoint,
} from "../features/tubulars/types";
import { ANN_ARBOR_HORSESHOE_SURVEY_M } from "./annArborHorseshoeSurvey";
import type {
  CasingReportData,
  CasingString,
  CasingTallyItem,
} from "../features/reports/forms/CasingForm/types";
import {
  buildSequence,
  generateOilfieldTimeLog,
  type OilfieldWellGeometry,
  type OilfieldWellPhase,
} from "./oilfieldTimeLog";
import {
  demoWellProgram,
  seededFloat,
  seededInt,
  seededUnit,
  type DemoWellProgram,
} from "./demoWellProgram";
import { generateDemoCostLogReports } from "./demoDailyCosts";
import { buildDemoStimulation } from "./demoStimulation";
import type { StimulationEntry } from "../features/stimulation/types";
// DETERMINISTIC MODE — ids and clock, for the generators that are BUILT rather than rolled.
//
// WHY. `generateOilfieldTestData` is now built once into a database and served read-only
// (DECISIONS.md 2026-09-03). A build artifact whose primary keys change on every build orphans
// everything that references them, and `Date.now()` + `Math.random()` ids do exactly that — the
// first build run produced a pad id of `id_1788472890665_27bblav1v` and the next would not match.
// It also fixes a defect that predates the database: in local mode the pad was re-minted with new
// ids on every seed, so nothing about it could be screenshot, asserted, or linked to.
//
// HONEST LIMIT. Ids are POSITIONAL — a counter in generation order. Inserting a bore or a report
// mid-sequence renumbers everything after it, so a structural change to the generator requires a
// database rebuild. That is the normal workflow for a build artifact, and the alternative (content
// -derived ids at 65 call sites) is not worth its cost here. It IS a trap if anyone ever persists
// user edits against these ids without rebuilding.
let deterministicIds: number | null = null;
let deterministicNow: string | null = null;

const generateId = (): string =>
  deterministicIds !== null
    ? `demo-${String(++deterministicIds).padStart(5, "0")}`
    : `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/** The generator's clock. Fixed in deterministic mode so a rebuild is byte-comparable. */
const nowIso = (): string => deterministicNow ?? new Date().toISOString();

// Random helpers.
//
// IN DETERMINISTIC MODE THEY ARE NOT RANDOM. Every draw is seeded off a counter, so the whole
// generator — not merely the parts that were seeded by hand — reproduces exactly. This is what
// closes the last of the `Math.random` sites that made the demo pad different on every reload:
// you could not demo the same number twice, screenshot it, or write a test that asserted one.
//
// The counter is SHARED with the id counter's ordering discipline: draws are positional, so
// inserting a call renumbers every draw after it. For a built artifact that means a rebuild, which
// is the normal workflow. `randomDate` additionally reads the clock, which `nowIso` fixes.
let deterministicDraw = 0;
const draw = (): number => seededUnit("gen-draw", ++deterministicDraw);
const rand = (): number => (deterministicIds !== null ? draw() : Math.random());

const randomInt = (min: number, max: number): number =>
  Math.floor(rand() * (max - min + 1)) + min;
const randomFloat = (min: number, max: number, decimals = 2): number =>
  parseFloat((rand() * (max - min) + min).toFixed(decimals));
const randomChoice = <T>(arr: T[]): T => arr[Math.floor(rand() * arr.length)]!;
const randomDate = (daysBack = 365): string => {
  const date = new Date(nowIso());
  date.setDate(date.getDate() - randomInt(0, daysBack));
  return date.toISOString().split("T")[0]!;
};
const randomTime = (): string => {
  const hours = randomInt(6, 22).toString().padStart(2, "0");
  const minutes = randomInt(0, 59).toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

// Constants for random data generation
const WORKING_FLUIDS = ["water", "methanol", "glycol"] as const;

// Base formation layers from Formations Data.xlsx
// Thicknesses stored so depths can be computed per-bore with random variation
const BASE_FORMATIONS = [
  {
    name: "Surface",
    thickness: 10.37,
    description: "Unconsolidated surface material",
    minerals: { sandstone: 100 },
    fluids: { water: 100 },
  },
  {
    name: "Drift",
    thickness: 39.63,
    description: "Glacial drift deposits",
    minerals: { shale: 40, sandstone: 20, clay: 40 },
    fluids: { water: 100 },
  },
  {
    name: "Sunbury Shale",
    thickness: 14.33,
    description: "Dark carbonaceous shale",
    minerals: { shale: 95, dolomite: 5 },
    fluids: { water: 100 },
  },
  {
    name: "Colwater Shale",
    thickness: 63.41,
    description: "Gray-green shale with dolomite interbeds",
    minerals: { shale: 80, dolomite: 20 },
    fluids: { water: 100 },
  },
  {
    name: "Traverse A",
    thickness: 28.66,
    description: "Upper Traverse limestone and shale",
    minerals: { shale: 65, dolomite: 35 },
    fluids: { brine: 100, brineSalinity: 30 },
  },
  {
    name: "Traverse B",
    thickness: 87.2,
    description: "Lower Traverse dolomite and limestone",
    minerals: { sandstone: 10, dolomite: 60, limestone: 30 },
    fluids: { brine: 100, brineSalinity: 30 },
  },
  {
    name: "Transition",
    thickness: 59.14,
    description: "Sandstone transition zone with hydrocarbon traces",
    minerals: { sandstone: 95, dolomite: 5 },
    fluids: { brine: 80, brineSalinity: 30, oil: 20, oilSG: 0.63 },
  },
  {
    name: "Detroit River",
    thickness: 4.27,
    description: "Clean sandstone, Detroit River group",
    minerals: { sandstone: 100 },
    fluids: { brine: 100, brineSalinity: 30 },
  },
  {
    name: "Bass Islands",
    thickness: 53.66,
    description: "Silurian dolomite and limestone",
    minerals: { dolomite: 5, limestone: 95 },
    fluids: { brine: 100, brineSalinity: 30 },
  },
];
// Legacy code arrays removed — time logs now use explicit 'DRILL' codes
const RIG_CLASSES = ["light", "medium", "heavy", "trailer", "truck", "track"];

// Large-dataset latitude (42°51'18"N, Macomb County) — the basis for LON_SPACING's cos-latitude factor.
// (START_LON was removed: the only consumer, the "Permian" oilfield pad, now anchors in West Texas.)
const START_LAT = 42 + 51 / 60 + 18 / 3600; // 42.855°
// Small dataset starting coordinates: 42°16'51"N 83°44'35"W (Ann Arbor / Washtenaw County)
const SMALL_START_LAT = 42 + 16 / 60 + 51 / 3600; // 42.2808°
const SMALL_START_LON = -(83 + 44 / 60 + 35 / 3600); // -83.7431°
// 20 ft on-center — standard institutional borefield spacing.
// Separate lat/lon increments for isotropic metric spacing at this latitude.
const GRID_SPACING_FT = 20; // feet
const GRID_SPACING_M = GRID_SPACING_FT * 0.3048; // 6.096 m
const LAT_SPACING = GRID_SPACING_M / 111320; // ~0.0000548 deg
const LON_SPACING =
  GRID_SPACING_M / (111320 * Math.cos((START_LAT * Math.PI) / 180)); // ~0.0000747 deg

// Compute northing/easting/tvd using minimum curvature between two stations
const computeMinCurv = (
  prev: {
    md: number;
    inc: number;
    azi: number;
    tvd: number;
    northing: number;
    easting: number;
  },
  cur: { md: number; inc: number; azi: number },
) => {
  const dMD = cur.md - prev.md;
  if (dMD <= 0)
    return { tvd: prev.tvd, northing: prev.northing, easting: prev.easting };
  const toRad = Math.PI / 180;
  const i1 = prev.inc * toRad,
    i2 = cur.inc * toRad;
  const a1 = prev.azi * toRad,
    a2 = cur.azi * toRad;
  const cosDL =
    Math.cos(i2 - i1) - Math.sin(i1) * Math.sin(i2) * (1 - Math.cos(a2 - a1));
  const dl = Math.acos(Math.min(1, Math.max(-1, cosDL)));
  const rf = dl === 0 ? 1 : (2 / dl) * Math.tan(dl / 2);
  const dTVD = (dMD / 2) * (Math.cos(i1) + Math.cos(i2)) * rf;
  const dN =
    (dMD / 2) *
    (Math.sin(i1) * Math.cos(a1) + Math.sin(i2) * Math.cos(a2)) *
    rf;
  const dE =
    (dMD / 2) *
    (Math.sin(i1) * Math.sin(a1) + Math.sin(i2) * Math.sin(a2)) *
    rf;
  return {
    tvd: prev.tvd + dTVD,
    northing: prev.northing + dN,
    easting: prev.easting + dE,
  };
};

// Generate survey points for a bore (with random deviation) — includes computed N/E/TVD
const generateSurveyPoints = (depth: number): SurveyPoint[] => {
  const raw: Array<{ md: number; inc: number; azi: number }> = [];
  const numStations = Math.floor(depth / 30) + 1;

  for (let i = 0; i <= numStations; i++) {
    const md = i * 30;
    if (md > depth) break;
    raw.push({
      md,
      inc: i === 0 ? 0 : randomFloat(0, 5, 2),
      azi: i === 0 ? 0 : randomFloat(0, 360, 2),
    });
  }

  const lastMd = raw[raw.length - 1]?.md || 0;
  if (lastMd < depth) {
    raw.push({
      md: depth,
      inc: randomFloat(0, 5, 2),
      azi: randomFloat(0, 360, 2),
    });
  }

  // Compute trajectory coordinates
  const points: SurveyPoint[] = [];
  let prev = { md: 0, inc: 0, azi: 0, tvd: 0, northing: 0, easting: 0 };
  for (const r of raw) {
    const calc =
      r.md === 0
        ? { tvd: 0, northing: 0, easting: 0 }
        : computeMinCurv(prev, r);
    points.push({
      md: r.md,
      inc: r.inc,
      azi: r.azi,
      tvd: calc.tvd,
      northing: calc.northing,
      easting: calc.easting,
    });
    prev = { ...r, ...calc };
  }

  return points;
};

// Generate vertical survey points (inc=0, azi=0 at all stations) — includes computed N/E/TVD
const generateVerticalSurveyPoints = (depth: number): SurveyPoint[] => {
  const points: SurveyPoint[] = [];
  const numStations = Math.floor(depth / 30) + 1;

  for (let i = 0; i <= numStations; i++) {
    const md = i * 30;
    if (md > depth) break;
    points.push({ md, inc: 0, azi: 0, tvd: md, northing: 0, easting: 0 });
  }

  const lastMd = points[points.length - 1]?.md || 0;
  if (lastMd < depth) {
    points.push({
      md: depth,
      inc: 0,
      azi: 0,
      tvd: depth,
      northing: 0,
      easting: 0,
    });
  }

  return points;
};

// Generate Time Log entries — sequential depth sections with depth-dependent drilling rate.
// Shallow sections drill faster (higher m/hr) and deep sections slower — so cost/m increases
// with depth, producing the expected upward trend in the drilling cost profile chart.
const generateTimeLogEntries = (boreDepth: number, count = 3) => {
  const entries = [];
  const sectionSize = boreDepth / count;
  // Base rate at surface: ~15 m/hr; decreases ~0.02 m/hr per meter of depth
  const baseRate = 15;
  const rateDecayPerM = 0.02;
  let currentDepth = 0;
  let currentMinute = randomInt(6, 8) * 60; // start between 6–8 AM

  for (let i = 0; i < count; i++) {
    const dStart = Math.round(currentDepth);
    const dEnd = Math.round(Math.min(currentDepth + sectionSize, boreDepth));
    const midDepth = (dStart + dEnd) / 2;
    // Drilling rate decreases with depth; add ±8% noise
    const nominalRate = Math.max(1, baseRate - rateDecayPerM * midDepth);
    const noiseFactor = 0.92 + Math.random() * 0.16; // 0.92–1.08
    const mPerHr = nominalRate * noiseFactor;
    const durationHrs = (dEnd - dStart) / mPerHr;
    const durationMins = Math.round(durationHrs * 60);

    const startMin = currentMinute;
    const endMin = currentMinute + durationMins;
    const fmt = (m: number) => {
      const h = Math.floor(m / 60) % 24;
      const mm = m % 60;
      return `${h.toString().padStart(2, "0")}:${mm.toString().padStart(2, "0")}`;
    };

    entries.push({
      id: generateId(),
      startTime: fmt(startMin),
      endTime: fmt(endMin),
      depthStart: dStart.toString(),
      depthEnd: dEnd.toString(),
      code1: "DRILL",
      code2: "",
      comment: `Drilling section ${i + 1}: ${dStart}–${dEnd} m`,
    });

    currentDepth = dEnd;
    currentMinute = endMin + randomInt(10, 30); // short break between sections
  }

  return entries;
};

// Generate job reports — 3 daily time-log reports covering the full bore depth sequentially.
// Total 9 DRILL entries, depth-increasing with realistic rate slowdown.
const generateJobReports = (boreDepth: number): Report[] => {
  // Generate 9 sequential drilling entries covering 0→boreDepth
  const allEntries = generateTimeLogEntries(boreDepth, 9);
  // Split into 3 daily reports of 3 entries each
  return [0, 1, 2].map((i) => ({
    id: generateId(),
    reportType: "time-log" as const,
    reportNumber: i + 1,
    date: randomDate(30 + i),
    data: { entries: allEntries.slice(i * 3, i * 3 + 3) },
  }));
};

// Generate drilling rig
const generateRig = (index: number): Rig => ({
  id: generateId(),
  rigName: `Rig ${index + 1}`,
  rigClass: randomChoice(RIG_CLASSES),
  substructureType: randomChoice([
    "skid",
    "trailer",
    "truck",
    "self-propelled",
    "crawler",
  ]),
  mastType: randomChoice(["telescopic", "folding", "fixed", "hydraulic"]),
  equipment: [
    {
      id: generateId(),
      role: "mud-pump",
      manufacturer: randomChoice([
        "National Oilwell",
        "Gardner Denver",
        "Mission",
      ]),
      model: `MP-${randomInt(100, 999)}`,
    },
    {
      id: generateId(),
      role: "top-drive",
      manufacturer: randomChoice(["Canrig", "Tesco", "Varco"]),
      model: `TD-${randomInt(100, 999)}`,
    },
  ],
});

// Generate formations (mud-log) report for a bore, truncated at boreDepth
const generateFormationsReport = (boreDepth: number): Report => {
  const formations: Array<Record<string, unknown>> = [];
  let currentDepth = 0;
  const sampleDate = randomDate(90);
  const geologist = `${randomChoice(FIRST_NAMES)} ${randomChoice(LAST_NAMES)}`;

  for (const layer of BASE_FORMATIONS) {
    if (currentDepth >= boreDepth) break;

    // Apply random thickness variation: ±0.5 to 1.5 meters
    const sign = Math.random() < 0.5 ? -1 : 1;
    const offset = randomFloat(0.5, 1.5, 2);
    const variedThickness = Math.max(1, layer.thickness + sign * offset);

    const depthFrom = currentDepth;
    let depthTo = currentDepth + variedThickness;

    // Truncate last layer at bore depth
    if (depthTo > boreDepth) depthTo = boreDepth;

    const row: Record<string, unknown> = {
      id: generateId(),
      sampleDate,
      sampleTime: randomTime(),
      geologist,
      bitDepth: depthTo.toFixed(2),
      sampleDepth: depthTo.toFixed(2),
      depthFrom: depthFrom.toFixed(2),
      depthTo: depthTo.toFixed(2),
      formationName: layer.name,
      description: layer.description,
      comments: "",
      images: [],
    };

    // Add mineral percentages as strings
    for (const [mineral, pct] of Object.entries(layer.minerals)) {
      row[mineral] = pct.toString();
    }

    // Add fluid values as strings
    for (const [fluid, val] of Object.entries(layer.fluids)) {
      row[fluid] = val.toString();
    }

    formations.push(row);
    currentDepth = depthTo;
  }

  return {
    id: generateId(),
    reportType: "mud-log",
    reportNumber: 1,
    date: sampleDate,
    data: { formations, user: geologist },
  };
};

// Unit conversion constants
const PSI_TO_KPA = 6.89476;
const GAL_TO_L = 3.78541;

// Gauge makes/models for pressure tests
const GAUGE_MAKES = ["Ashcroft", "WIKA", "Dwyer", "Winters"];
const GAUGE_MODELS = ["1009", "2089", "1279", "A-10", "PFQ"];

// Additive/fluid constants
const ADDITIVE_NAMES = [
  "Bentonite",
  "Barite",
  "Polymer",
  "Soda Ash",
  "Caustic Soda",
  "PAC (Polyanionic Cellulose)",
  "Lubricant",
];
const ADDITIVE_PURPOSES = [
  "Viscosity",
  "Weight",
  "Fluid Loss",
  "pH Control",
  "Shale Inhibition",
  "Fluid Loss",
  "Lubrication",
];
const ADDITIVE_UNITS: string[] = ["sack", "bbl", "gal", "lb"];

// Generate drilling fluid additives report
const generateAdditivesReport = (date: string): Report => {
  const numRows = randomInt(3, 5);
  const rows = [];

  for (let i = 0; i < numRows; i++) {
    const idx = i % ADDITIVE_NAMES.length;
    const start = randomInt(5, 50);
    const rec = randomInt(0, 20);
    const used = randomInt(1, Math.min(start + rec, 30));
    rows.push({
      id: generateId(),
      additive: ADDITIVE_NAMES[idx],
      purpose: ADDITIVE_PURPOSES[idx],
      unit: randomChoice(ADDITIVE_UNITS),
      costPerUnit: randomFloat(5, 50, 2).toString(),
      start: start.toString(),
      rec: rec.toString(),
      used: used.toString(),
      trans: "0",
    });
  }

  return {
    id: generateId(),
    reportType: "mud-additives",
    reportNumber: 1,
    date,
    // Page model (FT-45 Tier 2): one report = one day; the former day-card
    // fields live at data top-level (no dayCards[]/subType).
    data: {
      date,
      primaryFluid: "water",
      secondaryFluid: "",
      comment: "",
      images: [],
      rows,
    },
  };
};

// Generate pressure test report (loop-test) with gauge calibration
const generatePressureTestReport = (): Report => {
  const testDate = randomDate(60);
  const technician = `${randomChoice(FIRST_NAMES)} ${randomChoice(LAST_NAMES)}`;
  const company = "OXBO Engineering";

  // Start at 150 psi; end heavily skewed toward 148 (power distribution)
  const startPsi = 150;
  const endPsi = parseFloat((148 - Math.pow(Math.random(), 4) * 28).toFixed(1));
  const result: "pass" | "fail" = endPsi < 140 ? "fail" : "pass";
  const volumeGal = randomFloat(0.1, 2.0, 1);

  // Gauge with 3 calibration readings at specified setpoint ranges
  const setpoints = [
    randomFloat(150, 180, 1),
    randomFloat(100, 120, 1),
    randomFloat(130, 140, 1),
  ];
  const gaugeReadings = setpoints.map((sp) => {
    const obs = parseFloat(
      (sp * (1 + (Math.random() * 0.04 - 0.02))).toFixed(1),
    );
    return {
      id: generateId(),
      setpointDisplay: sp.toString(),
      observationDisplay: obs.toString(),
      setpointKPa: (sp * PSI_TO_KPA).toFixed(4),
      observationKPa: (obs * PSI_TO_KPA).toFixed(4),
    };
  });

  const gauge = {
    id: generateId(),
    make: randomChoice(GAUGE_MAKES),
    model: randomChoice(GAUGE_MODELS),
    serial: `SN-${randomInt(10000, 99999)}`,
    calibrationDate: randomDate(180),
    nistTraceable: true,
    readings: gaugeReadings,
  };

  return {
    id: generateId(),
    reportType: "pressure-test",
    reportNumber: 1,
    date: testDate,
    data: {
      subType: "loop-test",
      displayUnits: "imperial",
      pressureUnit: "psi",
      volumeUnit: "gal",
      entries: [
        {
          id: generateId(),
          pressureUnit: "psi",
          volumeUnit: "gal",
          testDate,
          technician,
          company,
          startTime: randomTime(),
          endTime: randomTime(),
          startPressureDisplay: startPsi.toString(),
          endPressureDisplay: endPsi.toString(),
          startPressureKPa: (startPsi * PSI_TO_KPA).toFixed(4),
          endPressureKPa: (endPsi * PSI_TO_KPA).toFixed(4),
          volumeAddedDisplay: volumeGal.toString(),
          volumeAddedL: (volumeGal * GAL_TO_L).toFixed(4),
          result,
          comment:
            result === "fail"
              ? "Pressure drop exceeded acceptable limits. Re-test required."
              : "Test passed. Pressure held within acceptable limits.",
          pictures: [],
          gauges: [gauge],
        },
      ],
    },
  };
};

// Generate thermal gradient report with readings at 10m increments to bore depth
const generateThermalGradientReport = (boreDepth: number): Report => {
  const testDate = randomDate(60);
  const technician = `${randomChoice(FIRST_NAMES)} ${randomChoice(LAST_NAMES)}`;
  const company = "OXBO Engineering";

  // Surface temperature ~14°C ± 0.5°C, gradient ~1°C per 75m
  const surfaceTemp = 14 + randomFloat(-0.5, 0.5, 2);
  const gradientRate = 1 / 75; // °C per meter

  // Readings at 10m increments from 0 to boreDepth
  const readings = [];
  for (let depth = 0; depth <= boreDepth; depth += 10) {
    const expectedTemp = surfaceTemp + depth * gradientRate;
    const noise = randomFloat(-0.3, 0.3, 2);
    const temp = parseFloat((expectedTemp + noise).toFixed(2));
    const tension = parseFloat((5 + randomFloat(-1, 1, 2)).toFixed(2));

    readings.push({
      id: generateId(),
      depthDisplay: depth.toFixed(2),
      temperatureDisplay: temp.toFixed(2),
      tensionDisplay: tension.toFixed(2),
      depthM: depth.toFixed(4),
      temperatureC: temp.toFixed(4),
      tensionKg: tension.toFixed(4),
    });
  }

  // Add final reading at exact bore depth if last increment didn't land on it
  const lastDepth =
    readings.length > 0
      ? parseFloat(readings[readings.length - 1]!.depthDisplay)
      : 0;
  if (lastDepth < boreDepth) {
    const expectedTemp = surfaceTemp + boreDepth * gradientRate;
    const temp = parseFloat(
      (expectedTemp + randomFloat(-0.3, 0.3, 2)).toFixed(2),
    );
    const tension = parseFloat((5 + randomFloat(-1, 1, 2)).toFixed(2));
    readings.push({
      id: generateId(),
      depthDisplay: boreDepth.toFixed(2),
      temperatureDisplay: temp.toFixed(2),
      tensionDisplay: tension.toFixed(2),
      depthM: boreDepth.toFixed(4),
      temperatureC: temp.toFixed(4),
      tensionKg: tension.toFixed(4),
    });
  }

  // Temperature sensor calibration — 2 setpoints: ~17°C ± 1°C, ~10°C ± 1°C
  const sp1 = parseFloat(randomFloat(16, 18, 1).toFixed(1));
  const sp2 = parseFloat(randomFloat(9, 11, 1).toFixed(1));
  const obs1 = parseFloat(
    (sp1 * (1 + (Math.random() * 0.04 - 0.02))).toFixed(2),
  );
  const obs2 = parseFloat(
    (sp2 * (1 + (Math.random() * 0.04 - 0.02))).toFixed(2),
  );

  const sensor = {
    id: generateId(),
    make: "Fluke",
    model: "1524",
    serial: `TC-${randomInt(10000, 99999)}`,
    calibrationDate: randomDate(180),
    nistTraceable: true,
    readings: [
      {
        id: generateId(),
        setpointDisplay: sp1.toString(),
        observationDisplay: obs1.toString(),
        setpointC: sp1.toFixed(4),
        observationC: obs1.toFixed(4),
      },
      {
        id: generateId(),
        setpointDisplay: sp2.toString(),
        observationDisplay: obs2.toString(),
        setpointC: sp2.toFixed(4),
        observationC: obs2.toFixed(4),
      },
    ],
  };

  return {
    id: generateId(),
    reportType: "thermal-gradient",
    reportNumber: 1,
    date: testDate,
    data: {
      temperatureUnit: "°C",
      depthUnit: "m",
      entries: [
        {
          id: generateId(),
          temperatureUnit: "°C",
          depthUnit: "m",
          weightUnit: "kg",
          testDate,
          technician,
          company,
          startTime: randomTime(),
          endTime: randomTime(),
          gradientReadings: readings,
          comment: "",
          pictures: [],
          sensors: [sensor],
        },
      ],
    },
  };
};

// Generate Loop Running report with jewelry items
const generateLoopRunningReport = (): Report => {
  const jewelryItems = Array.from({ length: randomInt(1, 3) }, () => {
    const lengthFt = randomFloat(5, 10, 1);
    const distFt = randomFloat(2, 20, 1);
    return {
      id: generateId(),
      length: lengthFt.toString(),
      lengthUnit: "ft",
      lengthM: parseFloat((lengthFt * 0.3048).toFixed(2)),
      weight: randomFloat(5, 25, 1).toString(),
      weightUnit: "kg",
      weightKg: randomFloat(5, 25, 1),
      material: "Steel",
      distanceFromBottom: distFt.toString(),
      distanceFromBottomUnit: "ft",
      distanceFromBottomM: parseFloat((distFt * 0.3048).toFixed(2)),
      leftInHole: true,
    };
  });

  return {
    id: generateId(),
    reportType: "loop-running",
    reportNumber: 1,
    date: randomDate(60),
    data: {
      loopDepth: randomFloat(200, 400, 0).toString(),
      loopDepthUnit: "m",
      startDate: randomDate(60),
      endDate: randomDate(30),
      startTime: randomTime(),
      endTime: randomTime(),
      runningCompany: randomChoice([
        "OXBO Loop Services",
        "GeoLoop Inc",
        "ThermalPipe Co",
      ]),
      conveyanceMethod: randomChoice(["Tremie Pipe", "Free Fall", "Weighted"]),
      loopMaterial: "HDPE",
      loopManufacturer: randomChoice([
        "Chevron Phillips",
        "ISCO",
        "Endot",
        "North American Pipe",
      ]),
      loopModel: `SDR-${randomChoice([11, 13.5, 17])}`,
      loopSerialNumber: `LP-${randomInt(10000, 99999)}`,
      loopFusionMethod: randomChoice([
        "Butt Fusion",
        "Electrofusion",
        "Socket Fusion",
      ]),
      loopInnerDiameter: randomFloat(0.75, 1.5, 2).toString(),
      loopInnerDiameterUnit: "in",
      loopOuterDiameter: randomFloat(1, 2, 2).toString(),
      loopOuterDiameterUnit: "in",
      loopThermalConductivity: randomFloat(0.3, 0.5, 2).toString(),
      loopThermalConductivityUnit: "W/mK",
      maxCompressiveLoad: randomFloat(500, 2000, 0).toString(),
      maxCompressiveLoadUnit: "N",
      avgCompressiveLoad: randomFloat(200, 800, 0).toString(),
      avgCompressiveLoadUnit: "N",
      jewelryItems,
      comments: "Loop installation completed without issues.",
    },
  };
};

// Motor makes/models for drilling tools
const MOTOR_MAKES = [
  "Dynapower",
  "National Oilwell",
  "Scientific Drilling",
  "Halliburton",
];
const MOTOR_MODELS = ["Mach 1", "PowerPak", "XL Series", "Navi-Drill"];
const MOTOR_STAGE_LOBES = ["1/2", "2/3", "3/4", "4/5", "5/6"];
const BIT_MAKES = [
  "Smith Bits",
  "Hughes Christensen",
  "Reed Hycalog",
  "Security DBS",
  "Varel",
];
const BIT_MODELS = ["MDi616", "GM404", "Stratapax", "FX76", "ToughCut"];
const MWD_COMPANIES = [
  "Schlumberger",
  "Halliburton",
  "Baker Hughes",
  "Scientific Drilling",
];
const MWD_TOOL_TYPES = [
  "Positive Pulse",
  "Negative Pulse",
  "EM",
  "Continuous Wave",
];

// Generate drilling tools (BHA/drillstring) report for a bore
const generateDrillingToolsReport = (boreDepth: number): Report => {
  // Inversely proportional motor factors:
  // t=0 → torqueFactor=1, rpmFactor=1.5
  // t=1 → torqueFactor=4, rpmFactor=0.6
  const t = Math.random();
  const torqueFactor = 1 + t * 3;
  const rpmFactor = 1.5 - t * 0.9;

  // Bit: 6" diameter, 6-8 nozzles at 6-10/32nds each
  const numNozzles = randomInt(6, 8);
  const nozzles: Array<{ position: number; diameter: string }> = [];
  for (let i = 0; i < numNozzles; i++) {
    nozzles.push({
      position: i + 1,
      diameter: randomInt(6, 10).toString(),
    });
  }

  // TFA = Σ π/4 * (d/32)² in²
  const tfa = nozzles.reduce((sum, n) => {
    const dInches = parseFloat(n.diameter) / 32;
    return sum + (Math.PI / 4) * dInches * dInches;
  }, 0);

  const jets = nozzles.map((n) => n.diameter).join("-");

  const drillstring = {
    id: generateId(),
    bhaName: `BHA Run 1`,
    isWired: false,
    runNumber: "1",
    startDate: randomDate(90),
    endDate: randomDate(30),
    depthStart: "0",
    depthEnd: boreDepth.toString(),
    bit: {
      make: randomChoice(BIT_MAKES),
      model: randomChoice(BIT_MODELS),
      serialNumber: `BIT-${randomInt(10000, 99999)}`,
      type: "PDC",
      diameter: "6",
      iadc: "M433",
      tfa: tfa.toFixed(3),
      jets,
      cutterCount: randomInt(12, 20).toString(),
      bladeCount: randomInt(4, 7).toString(),
      reasonPulled: "",
      nozzles,
    },
    motor: {
      make: randomChoice(MOTOR_MAKES),
      model: randomChoice(MOTOR_MODELS),
      serialNumber: `MTR-${randomInt(10000, 99999)}`,
      stageLobe: randomChoice(MOTOR_STAGE_LOBES),
      length: randomFloat(12, 22, 1).toString(),
      bendAngle: randomFloat(0.5, 2.0, 2).toString(),
      bendSetting: randomChoice(["1", "2", "3"]),
      bitToBend: randomFloat(3, 6, 1).toString(),
      minFlow: randomInt(80, 120).toString(),
      maxFlow: randomInt(200, 350).toString(),
      maxWob: randomInt(8, 20).toString(),
      maxRpm: randomInt(100, 250).toString(),
      maxDiffPressure: randomInt(400, 800).toString(),
      torqueFactor: torqueFactor.toFixed(2),
      rpmFactor: rpmFactor.toFixed(2),
    },
    mwd: {
      company: randomChoice(MWD_COMPANIES),
      operator: `${randomChoice(FIRST_NAMES)} ${randomChoice(LAST_NAMES)}`,
      make: randomChoice(MWD_COMPANIES),
      model: `MWD-${randomInt(100, 999)}`,
      serialNumber: `MWD-${randomInt(10000, 99999)}`,
      toolType: randomChoice(MWD_TOOL_TYPES),
      sensors: "Inc, Azi, TF, Gamma",
      surveyFreq: randomChoice(["30", "60", "90"]),
      transmissionType: "Mud pulse",
      motorOffsetAngle: randomFloat(0, 5, 1).toString(),
      distanceToBit: randomFloat(8, 15, 1).toString(),
    },
    agitator: {
      make: "",
      model: "",
      serialNumber: "",
      frequency: "",
      amplitude: "",
      maxDeltaP: "",
    },
    jars: {
      make: "",
      model: "",
      serialNumber: "",
      type: "",
      tripForce: "",
      jarStroke: "",
      od: "",
      innerId: "",
      length: "",
      weight: "",
    },
    lwd: {
      service: "",
      toolType: "",
      make: "",
      model: "",
      serialNumber: "",
      od: "",
      length: "",
      distanceToBit: "",
      sensors: "",
    },
    components: [
      {
        id: generateId(),
        sequence: 1,
        type: "Drill Pipe",
        description: '3.5" DP',
        od: "3.5",
        innerId: "2.764",
        length: (boreDepth - 30).toString(),
        weight: "13.3",
        grade: "S-135",
        connection: "NC38",
      },
      {
        id: generateId(),
        sequence: 2,
        type: "Drill Collar",
        description: '4.75" DC',
        od: "4.75",
        innerId: "2.25",
        length: "30",
        weight: "47",
        grade: "Non-Mag",
        connection: "NC38",
      },
    ],
    stabilizers: [],
  };

  return {
    id: generateId(),
    reportType: "drilling-tools",
    reportNumber: 1,
    date: drillstring.startDate,
    data: {
      drillstrings: [drillstring],
    },
  };
};

// Generate mud system check report — fluid properties for hydraulic MSE
const generateMudCheckReport = (date: string): Report => {
  const density = randomFloat(8.3, 9.5, 1);
  return {
    id: generateId(),
    reportType: "mud-properties",
    reportNumber: 1,
    date,
    // Page model (FT-45 Tier 2): report type `mud-properties`, day-level `date`,
    // that day's samples in `entries` (no `subType`).
    data: {
      date,
      entries: [
        {
          id: generateId(),
          date,
          time: randomTime(),
          technician: `${randomChoice(FIRST_NAMES)} ${randomChoice(LAST_NAMES)}`,
          company: "Basin Geoscience",
          properties: {
            density: density.toString(),
            fluidTemp: randomFloat(65, 95, 0).toString(),
            pH: randomFloat(8.0, 10.0, 1).toString(),
            funnelVis: randomFloat(30, 55, 0).toString(),
            plasticVis: randomFloat(8, 20, 0).toString(),
            yieldPoint: randomFloat(5, 18, 0).toString(),
          },
          comments: "",
          images: [],
          collapsedSections: {
            Basic: false,
            Viscosity: false,
            Solids: true,
            Chemistry: true,
            Composition: true,
            Filtration: true,
            Gel: true,
            Fann: true,
          },
        },
      ],
    },
  };
};

/**
 * DAILY MUD CHECKS — one `mud-properties` report per rig day (PO 2026-09-03, verbatim: "I don't
 * even care if the mud check is the same for every drilling day. just so that there is one for each
 * day.").
 *
 * It is driven off the SAME day program the cost log and the mud log read, so all three agree about
 * which day was which. The properties vary a little by hole section rather than being identical —
 * a check that reads 8.9 ppg in the lateral as well as at surface is the kind of detail a drilling
 * engineer notices immediately, and the variation costs nothing.
 *
 * NO ENGINEERING MATH (Charter Abs-2). The numbers below are AUTHORED per-section bands that a
 * seed picks from. Nothing computes a mud weight from a pore pressure, an ECD, or a hydrostatic
 * gradient — those are engineering calculations and belong in canonical Python. These are demo
 * values chosen to look plausible, and nothing may cite them as a design basis (Abs-1).
 */
const generateDailyMudCheckReports = (
  program: DemoWellProgram,
  wellName: string,
): Report[] => {
  // Authored bands per section. Mud gets heavier and hotter with depth on a real well, so the
  // bands step that way — but they are picked, not derived.
  const BANDS: Record<
    string,
    { density: [number, number]; temp: [number, number]; fv: [number, number] }
  > = {
    mobilization: { density: [8.4, 8.7], temp: [70, 85], fv: [28, 34] },
    surface: { density: [8.4, 8.9], temp: [75, 95], fv: [30, 38] },
    intermediate: { density: [9.2, 10.4], temp: [95, 120], fv: [36, 46] },
    curve: { density: [10.6, 11.8], temp: [120, 140], fv: [42, 52] },
    lateral: { density: [11.4, 12.6], temp: [130, 155], fv: [45, 58] },
  };

  const technician = `${randomChoice(FIRST_NAMES)} ${randomChoice(LAST_NAMES)}`;
  const reports: Report[] = [];

  for (const day of program.days) {
    // No mud system before the rig is up: the first mobilization days have nothing to check.
    if (day.section === "mobilization" && day.dayNo < 3) continue;
    const b = BANDS[day.section] ?? BANDS.surface!;
    const seed = [wellName, day.dayNo] as const;
    const density = seededFloat(b.density[0], b.density[1], 1, "mw", ...seed);

    reports.push({
      id: `${wellName}-mud-${day.dayNo}`,
      reportType: "mud-properties",
      reportNumber: day.dayNo,
      date: day.date,
      data: {
        date: day.date,
        entries: [
          {
            id: `${wellName}-mud-${day.dayNo}-1`,
            date: day.date,
            time: "06:00",
            technician,
            company: "Basin Geoscience",
            properties: {
              density: density.toString(),
              fluidTemp: seededFloat(
                b.temp[0],
                b.temp[1],
                0,
                "mt",
                ...seed,
              ).toString(),
              pH: seededFloat(8.6, 9.8, 1, "ph", ...seed).toString(),
              funnelVis: seededFloat(
                b.fv[0],
                b.fv[1],
                0,
                "fv",
                ...seed,
              ).toString(),
              plasticVis: seededFloat(9, 24, 0, "pv", ...seed).toString(),
              yieldPoint: seededFloat(6, 22, 0, "yp", ...seed).toString(),
            },
            comments: day.flat
              ? "Circulating and conditioning — no new hole."
              : "",
            images: [],
            collapsedSections: {
              Basic: false,
              Viscosity: false,
              Solids: true,
              Chemistry: true,
              Composition: true,
              Filtration: true,
              Gel: true,
              Fann: true,
            },
          },
        ],
      },
    } as unknown as Report);
  }
  return reports;
};

// Generate project personnel — 2 people per default role
const generateProjectPersonnel = () => [
  {
    id: generateId(),
    firstName: "Margaret",
    lastName: "Thornton",
    role: "Asset Owner",
    organization: "Thornton Properties",
    email: "mthornton@thorntonprop.com",
    phone: "(312) 555-0142",
  },
  {
    id: generateId(),
    firstName: "Richard",
    lastName: "Cavanagh",
    role: "Asset Owner",
    organization: "Lakeshore Holdings",
    email: "rcavanagh@lakeshoreh.com",
    phone: "(312) 555-0198",
  },
  {
    id: generateId(),
    firstName: "Susan",
    lastName: "Obermann",
    role: "Project Management",
    organization: "GeoEnergy Solutions",
    email: "sobermann@geoenergy.com",
    phone: "(734) 555-0231",
  },
  {
    id: generateId(),
    firstName: "Kevin",
    lastName: "Ratliff",
    role: "Project Management",
    organization: "GeoEnergy Solutions",
    email: "kratliff@geoenergy.com",
    phone: "(734) 555-0244",
  },
  {
    id: generateId(),
    firstName: "Patricia",
    lastName: "Voss",
    role: "Design/Engineering",
    organization: "Meridian Engineering",
    email: "pvoss@meridianeng.com",
    phone: "(248) 555-0317",
  },
  {
    id: generateId(),
    firstName: "Andrew",
    lastName: "Kaszynski",
    role: "Design/Engineering",
    organization: "Meridian Engineering",
    email: "akaszynski@meridianeng.com",
    phone: "(248) 555-0329",
  },
  {
    id: generateId(),
    firstName: "Carlos",
    lastName: "Medina",
    role: "Drilling Contractor",
    organization: "Great Lakes Drilling",
    email: "cmedina@gldrill.com",
    phone: "(517) 555-0405",
  },
  {
    id: generateId(),
    firstName: "Brenda",
    lastName: "Kowalski",
    role: "Drilling Contractor",
    organization: "Great Lakes Drilling",
    email: "bkowalski@gldrill.com",
    phone: "(517) 555-0418",
  },
  {
    id: generateId(),
    firstName: "Thomas",
    lastName: "Eriksen",
    role: "Geologist",
    organization: "Basin Geoscience",
    email: "teriksen@basingeo.com",
    phone: "(616) 555-0502",
  },
  {
    id: generateId(),
    firstName: "Laura",
    lastName: "Cheng",
    role: "Geologist",
    organization: "Basin Geoscience",
    email: "lcheng@basingeo.com",
    phone: "(616) 555-0519",
  },
  {
    id: generateId(),
    firstName: "David",
    lastName: "Prentice",
    role: "Thermal Tester",
    organization: "ThermoProbe Services",
    email: "dprentice@thermoprobe.com",
    phone: "(269) 555-0601",
  },
  {
    id: generateId(),
    firstName: "Jennifer",
    lastName: "Nakamura",
    role: "Thermal Tester",
    organization: "ThermoProbe Services",
    email: "jnakamura@thermoprobe.com",
    phone: "(269) 555-0614",
  },
  {
    id: generateId(),
    firstName: "Marcus",
    lastName: "DiStefano",
    role: "Field User",
    organization: "Great Lakes Drilling",
    email: "mdistefano@gldrill.com",
    phone: "(517) 555-0703",
  },
  {
    id: generateId(),
    firstName: "Angela",
    lastName: "Birch",
    role: "Field User",
    organization: "GeoEnergy Solutions",
    email: "abirch@geoenergy.com",
    phone: "(734) 555-0718",
  },
];

// Generate a small test borefield (6 wells)
export const generateSmallTestData = (): Borefield => {
  const rigs: Rig[] = [generateRig(0)];
  const bores: Bore[] = [];

  // Pre-generate depths so we can find the max for planned surveys
  const depths = Array.from({ length: 6 }, () => randomInt(200, 400));
  const maxDepth = Math.max(...depths);

  for (let i = 0; i < 6; i++) {
    const row = Math.floor(i / 3);
    const col = i % 3;
    const lat = SMALL_START_LAT + row * LAT_SPACING;
    const lon = SMALL_START_LON + col * LON_SPACING;
    const depth = depths[i]!;
    const latStr = lat.toFixed(6);
    const lonStr = lon.toFixed(6);

    const smallMwdId = generateId();
    const bore: Bore = {
      id: generateId(),
      boreName: `Well ${String(i + 1).padStart(2, "0")}`,
      boreType: "Single U-Tube",
      status: randomChoice(["Completed", "Drilling", "Planned"]),
      latitude: latStr,
      longitude: lonStr,
      elevation: randomFloat(800, 900, 1).toString(),
      designDepth: maxDepth.toString(),
      actualDepth: depth.toString(),
      boreDiameter: randomChoice(["4", "5", "6"]),
      loopDepth: (depth - randomInt(1, 5)).toString(),
      loopDepthUnit: "m",
      workingFluid: randomChoice([...WORKING_FLUIDS]),
      comment: `Test bore ${i + 1}`,
      loopDetails: {
        material: "HDPE",
        make: "Quality Tubing",
        model: "Thermaster 9030",
        innerDiameter: "38",
        innerDiameterUnit: "mm",
        outerDiameter: "54",
        outerDiameterUnit: "mm",
        thermalConductivity: "0.15",
        thermalConductivityUnit: "W/mK",
        burstRating: "2413",
        burstRatingUnit: "kPa",
        fusionMethod: "butt",
        utubeMaterial: "HDPE",
        utubeMake: "Quality Tubing",
        utubeModel: "Thermaster 9030",
        utubeInnerDiameter: "38",
        utubeInnerDiameterUnit: "mm",
        utubeOuterDiameter: "54",
        utubeOuterDiameterUnit: "mm",
        utubeThermalConductivity: "0.15",
        utubeThermalConductivityUnit: "W/mK",
        utubeFusionMethod: "butt",
      },
      groutDetails: {
        slurryType: "Grout - Thermally Enhanced",
        manufacturer: "Bariod",
        productName: "LordKelvinsBlend",
        density: "1258",
        densityUnit: "kg/m3",
        thermalConductivity: "3.5",
        thermalConductivityUnit: "W/mK",
        compressiveStrength: "10342",
        compressiveStrengthUnit: "kPa",
      },
      activeSurveyId: smallMwdId,
      surveys: [
        {
          id: generateId(),
          name: `Well ${String(i + 1).padStart(2, "0")} Planned`,
          type: "planned" as const,
          date: randomDate(180),
          inputMode: "vertical" as const,
          latitude: latStr,
          longitude: lonStr,
          points: generateVerticalSurveyPoints(maxDepth),
          createdAt: nowIso(),
        },
        {
          id: smallMwdId,
          name: `Well ${String(i + 1).padStart(2, "0")} Survey`,
          type: "mwd" as const,
          date: randomDate(90),
          latitude: latStr,
          longitude: lonStr,
          points: generateSurveyPoints(depth),
          createdAt: nowIso(),
        },
      ],
      jobs: [
        {
          id: generateId(),
          jobType: "construction",
          jobNumber: `Construction - Well ${String(i + 1).padStart(2, "0")}`,
          status: "In Progress",
          startDate: new Date(
            Date.now() - randomInt(30, 180) * 86400000,
          ).toISOString(),
          rigId: rigs[0]!.id,
          reports: [
            ...generateJobReports(depth),
            generateFormationsReport(depth),
            generateAdditivesReport(randomDate(30)),
            generatePressureTestReport(),
            generateThermalGradientReport(depth),
            generateLoopRunningReport(),
            generateDrillingToolsReport(depth),
            generateMudCheckReport(randomDate(30)),
          ],
        },
      ],
      createdAt: nowIso(),
      modifiedAt: nowIso(),
    };

    bores.push(bore);
  }

  return {
    id: generateId(),
    borefieldName: "Small Test Borefield",
    ownerName: "OXBO Engineering",
    operatorName: "OXBO Operations",
    systemType: "Closed Loop",
    status: "Drilling",
    address: {
      street: "123 Test Lane",
      city: "",
      stateProvince: "MI",
      postalCode: "48109",
      countyParish: "Washtenaw",
    },
    geodetic: {
      fipsCode: "2113",
      declination: "-6.5",
      datum: "NAD83",
      elevationDatum: "NAVD88",
    },
    bores,
    rigs,
    defaultRigId: rigs[0]!.id,
    actualBoreCount: 6,
    isSingleBore: false,
    planning: {
      kpiTargets: {
        plannedBores: 6,
        plannedDrillingDays: 20,
        plannedCostPerBore: 100000,
        minimumFieldFFactor: 0.8,
        targetDeviationLimit: 2,
        rigCalculationFactors: {
          drillingDaysPerBore: 1.5,
          loopRunningDaysPerBore: 1.0,
          rigUptimeFactor: 0.7,
          rigExternalFactor: 0.8,
          rigGeologicFactor: 0.95,
        },
        rigHourlyRate: 1000,
        maxDrillableDepth: 400,
        fixedCostPerBore: null,
      },
      checklist: {},
      responsiblePersons: generateProjectPersonnel(),
    },
    createdAt: nowIso(),
    modifiedAt: nowIso(),
  };
};

// Cost/personnel log constants
const FIRST_NAMES = [
  "John",
  "Mike",
  "Dave",
  "Tom",
  "Chris",
  "James",
  "Robert",
  "Daniel",
  "Mark",
  "Steve",
];
const LAST_NAMES = [
  "Smith",
  "Johnson",
  "Williams",
  "Brown",
  "Jones",
  "Davis",
  "Wilson",
  "Taylor",
  "Clark",
  "Hall",
];

// =============================================================================
// OILFIELD HORIZONTAL WELL TEST DATA
// 10 horizontal bores with directional surveys, Permian-style geology,
// and a full oilfield casing program for smoke-testing horizontal features
// =============================================================================

// Delaware Basin stratigraphic column — depth ranges in METRES (SI). `maxDepth` is the BASE of
// each formation; the lookup is `find(midDepth < f.maxDepth)`, so the list must run shallow→deep.
//
// CORRECTED 2026-09-03. Three defects, all of which a Permian engineer spots at a glance:
//
//   1. THE TOPS WERE COMPRESSED ~2.5x. Wolfcamp B sat at 1463 m (4,800 ft). Real Delaware Basin
//      Wolfcamp is 11,000–12,500 ft. That is why a 20,000 ft MD well — which is what a modern
//      Wolfcamp lateral IS — could not be reconciled with this data.
//   2. WOLFCAMP A WAS BELOW WOLFCAMP B. A is the shallower, younger bench; B sits beneath it.
//      The file had A at Infinity and B above it, i.e. inverted.
//   3. THE STACK DID NOT MATCH THE COORDINATES. These names — Salado, Rustler, Dewey Lake, Bone
//      Spring — are DELAWARE Basin stratigraphy, while the pad sat at 31.9 / -102.0, which is
//      Midland County. Different basin, different column: the Midland side has Spraberry and Dean
//      and no thick Salado salt. The pad has been moved west to Loving County to match the column
//      that was already written, rather than rewriting the geology to match a coordinate.
//
// Castile and the Delaware Mountain Group (Bell Canyon) are ADDED — a Delaware column without them
// jumps from salt straight to Bone Spring, which is a 4,000 ft gap.
//
// SALADO IS A DRILLING STORY, not just a label: a ~2,000 ft salt section is where you take
// washouts, where hole cleaning gets hard, and why the intermediate string exists at all.
const OILFIELD_FORMATIONS = [
  {
    name: "Surface / Alluvium",
    maxDepth: 30, // ~100 ft
    description: "Unconsolidated surface material",
    minerals: { sandstone: 100 },
    fluids: { water: 100 },
  },
  {
    name: "Ogallala",
    maxDepth: 152, // ~500 ft — the shallow fresh-water sand the surface string protects
    description: "Sand and clay deposits — fresh-water aquifer",
    minerals: { sandstone: 60, clay: 40 },
    fluids: { water: 100 },
  },
  {
    name: "Dockum",
    maxDepth: 457, // ~1,500 ft
    description: "Red beds — shale and sandstone",
    minerals: { shale: 70, sandstone: 30 },
    fluids: { water: 100 },
  },
  {
    name: "Dewey Lake",
    maxDepth: 610, // ~2,000 ft
    description: "Siltstone and shale",
    minerals: { siltstone: 50, shale: 50 },
    fluids: { brine: 100, brineSalinity: 15 },
  },
  {
    name: "Rustler",
    maxDepth: 792, // ~2,600 ft
    description: "Dolomite with anhydrite — lost-circulation risk",
    minerals: { dolomite: 60, anhydrite: 40 },
    fluids: { brine: 100, brineSalinity: 40 },
  },
  {
    name: "Salado",
    maxDepth: 1402, // ~4,600 ft — ~2,000 ft of mobile salt
    description: "Massive halite with anhydrite interbeds — washout and creep",
    minerals: { halite: 80, anhydrite: 20 },
    fluids: { brine: 100, brineSalinity: 260 },
  },
  {
    name: "Castile",
    maxDepth: 1676, // ~5,500 ft
    description: "Anhydrite and halite — base of the evaporite section",
    minerals: { anhydrite: 65, halite: 35 },
    fluids: { brine: 100, brineSalinity: 200 },
  },
  {
    name: "Bell Canyon",
    maxDepth: 2286, // ~7,500 ft — Delaware Mountain Group
    description: "Sandstone and siltstone — Delaware Mountain Group",
    minerals: { sandstone: 70, siltstone: 30 },
    fluids: { brine: 100, brineSalinity: 120 },
  },
  {
    name: "Bone Spring",
    maxDepth: 3353, // ~11,000 ft — a target in its own right
    description: "Interbedded carbonate, sand and shale — productive",
    minerals: { limestone: 45, sandstone: 30, shale: 25 },
    fluids: { oil: 70, brine: 30, brineSalinity: 90 },
  },
  {
    name: "Wolfcamp A",
    maxDepth: 3597, // ~11,800 ft — the SHALLOWER bench. A sits above B.
    description: "Organic-rich carbonate mudstone — primary landing zone",
    minerals: { limestone: 50, shale: 40, sandstone: 10 },
    fluids: { oil: 80, brine: 20, brineSalinity: 80 },
  },
  {
    name: "Wolfcamp B",
    maxDepth: Infinity, // ~12,500 ft and deeper
    description: "Organic-rich shale and carbonate — secondary landing zone",
    minerals: { shale: 60, limestone: 35, sandstone: 5 },
    fluids: { oil: 75, brine: 25, brineSalinity: 85 },
  },
];

/** Generate formations report for an oilfield horizontal well (samples every ~30m). */
/**
 * The MUD LOG — one lithology sample every 30 m from surface to TD, over the Delaware section.
 *
 * @param totalMD  the well's measured depth
 * @param program  the day-by-day drilling program, so each sample is dated THE DAY THE BIT PASSED
 *                 THAT DEPTH. Previously one `randomDate(90)` stamped every sample in the well —
 *                 a single date, 90 days back from today, which after the pad moved to a 2018
 *                 campaign put every mud-log sample in 2026 on a well drilled in March 2018. A mud
 *                 log that disagrees with the time log about when a formation was penetrated is
 *                 worse than no mud log: it is the report a geologist checks the depth against.
 */
const generateOilfieldFormationsReport = (
  totalMD: number,
  program?: DemoWellProgram,
): Report => {
  const geologist = `${randomChoice(FIRST_NAMES)} ${randomChoice(LAST_NAMES)}`;

  /** The date the bit made this depth, from the day program, so the mud log agrees with the time
   *  log and the cost log about when a formation was penetrated. */
  const dateAtDepth = (depth: number): string => {
    if (!program) return randomDate(90);
    const drilled = program.days.filter((d) => d.depthTo > d.depthFrom);
    const first = drilled[0];
    const last = drilled[drilled.length - 1];
    if (!first || !last) return program.days[0]!.date;
    // Above the first drilled metre: the conductor is driven before the rig arrives, so no drilling
    // day covers it and a naive lookup fell through to the LAST day — dating the shallowest
    // cuttings after TD.
    if (depth <= first.depthFrom) return first.date;
    for (const d of drilled) {
      if (depth >= d.depthFrom && depth <= d.depthTo) return d.date;
    }
    return last.date;
  };

  // ── ONE LAYER PER FORMATION, WITH ITS SAMPLES NESTED ──────────────────────────────────────────
  //
  // An earlier version emitted one legacy `FormationRow` per 30 m cuttings sample — 211 of them —
  // and the v1→v2 adapter maps a row to a LAYER 1:1. So the Formations page rendered 211 "layers",
  // showing Dockum ten times in a row instead of one Dockum with a top and a base. A mud log has
  // formation TOPS and, separately, many samples within each: `FormationLayer.samples` is 1:N and
  // says so. This writes the canonical v2 shape directly (`v: 2`), so nothing has to migrate it.
  const SAMPLE_INTERVAL_M = 30;
  const layers: Record<string, unknown>[] = [];

  for (const [i, layer] of OILFIELD_FORMATIONS.entries()) {
    const topDepth = i === 0 ? 0 : OILFIELD_FORMATIONS[i - 1]!.maxDepth;
    const bottomDepth = Math.min(layer.maxDepth, totalMD);
    if (topDepth >= totalMD) break;

    // Fractions are 0-1 in the canonical model; the source constants are percentages summing to
    // 100, so this is a unit change, not a calculation.
    const minerals = Object.entries(layer.minerals).map(([mineral, pct]) => ({
      mineral: mineral.charAt(0).toUpperCase() + mineral.slice(1),
      fraction: pct / 100,
    }));
    const f = layer.fluids as Record<string, number>;
    const fluids = {
      water: (f.water ?? 0) / 100,
      brine: (f.brine ?? 0) / 100,
      oil: (f.oil ?? 0) / 100,
      gas: (f.gas ?? 0) / 100,
      ...(f.brineSalinity !== undefined
        ? { brineSalinity: f.brineSalinity * 1000 }
        : {}),
    };

    // Cuttings samples through this formation, on the same 30 m interval as before.
    const samples: Record<string, unknown>[] = [];
    for (
      let md = Math.ceil(topDepth / SAMPLE_INTERVAL_M) * SAMPLE_INTERVAL_M;
      md < bottomDepth;
      md += SAMPLE_INTERVAL_M
    ) {
      samples.push({
        id: `${generateId()}`,
        sampleDepthM: md,
        sampleDate: dateAtDepth(md),
        sampleTime: randomTime(),
        loggerName: geologist,
        notes: "",
      });
    }

    layers.push({
      id: generateId(),
      name: layer.name,
      topDepth,
      bottomDepth,
      pickStatus: "while-drilling",
      minerals,
      fluids,
      samples,
    });
  }

  const lastLayer = layers[layers.length - 1];
  const lastSamples = (lastLayer?.samples ?? []) as Record<string, unknown>[];
  const lastDate =
    (lastSamples[lastSamples.length - 1]?.["sampleDate"] as
      | string
      | undefined) ?? dateAtDepth(totalMD);

  return {
    id: generateId(),
    reportType: "mud-log",
    reportNumber: 1,
    // The log closes on its last sample — the day logging finished.
    date: lastDate,
    // v2 CANONICAL. Written directly so the screen takes the modern path and the one-shot legacy
    // migration never fires on generated data.
    data: { v: 2, formations: layers, user: geologist },
  };
};

/** Generate directional survey for a horizontal well. */
const generateHorizontalSurveyPoints = (
  kopMD: number,
  landingMD: number,
  totalMD: number,
  targetAzi: number,
): SurveyPoint[] => {
  const raw: Array<{ md: number; inc: number; azi: number }> = [];
  const stationInterval = 100;

  for (let md = 0; md <= totalMD; md += stationInterval) {
    let inc: number;
    let azi: number;

    if (md === 0) {
      inc = 0;
      azi = 0;
    } else if (md <= kopMD) {
      // Vertical section — near-zero inclination, steady azimuth
      inc = randomFloat(0, 0.5, 2);
      azi = targetAzi + randomFloat(-2, 2, 2);
    } else if (md <= landingMD) {
      // Build section — increase inclination ~10°/100ft
      const buildFraction = (md - kopMD) / (landingMD - kopMD);
      inc = Math.min(90, buildFraction * 90) + randomFloat(-0.5, 0.5, 2);
      inc = Math.max(0, Math.min(90, inc));
      azi = targetAzi + randomFloat(-2, 2, 2);
    } else {
      // Lateral — hold 90°
      inc = 90 + randomFloat(-0.3, 0.3, 2);
      azi = targetAzi + randomFloat(-1, 1, 2);
    }

    // Normalize azimuth to 0-360
    azi = ((azi % 360) + 360) % 360;
    raw.push({ md, inc, azi });
  }

  // Add final station at TD if not already there
  const lastMd = raw[raw.length - 1]?.md || 0;
  if (lastMd < totalMD) {
    raw.push({
      md: totalMD,
      inc: 90 + randomFloat(-0.3, 0.3, 2),
      azi: (((targetAzi + randomFloat(-1, 1, 2)) % 360) + 360) % 360,
    });
  }

  // Compute trajectory via min-curvature
  const points: SurveyPoint[] = [];
  let prev = { md: 0, inc: 0, azi: 0, tvd: 0, northing: 0, easting: 0 };
  for (const r of raw) {
    const calc =
      r.md === 0
        ? { tvd: 0, northing: 0, easting: 0 }
        : computeMinCurv(prev, r);
    points.push({
      md: r.md,
      inc: r.inc,
      azi: r.azi,
      tvd: calc.tvd,
      northing: calc.northing,
      easting: calc.easting,
    });
    prev = { ...r, ...calc };
  }

  return points;
};

/** Generate oilfield casing program for a horizontal well. */
const generateOilfieldTubularProgram = (
  totalMD: number,
  kopMD: number,
  landingMD: number,
): TubularProgram => {
  const now = nowIso();
  let seq = 1;
  let idCounter = 0;
  // Same deterministic-mode contract as `generateId`: a built artifact cannot carry wall-clock ids.
  const mid = () =>
    deterministicIds !== null
      ? `oilfield-${String(++idCounter).padStart(4, "0")}`
      : `oilfield-${Date.now()}-${++idCounter}`;

  const mkCement = (
    topMD: number,
    bottomMD: number,
    type: string,
    density: number,
  ): CementInterval => ({
    id: mid(),
    topMD,
    bottomMD,
    cementType: type,
    density,
  });

  const mkJoint = (
    length: number,
    od: number | null,
    innerDia: number | null,
    weight: number | null,
    grade: string,
    material: string,
    connection: string,
  ): TallyJoint[] => [
    {
      id: mid(),
      sequence: 1,
      type: "Standard Joint",
      description: "Full string",
      length,
      od,
      innerDiameter: innerDia,
      weight,
      grade,
      material,
      connectionType: connection,
    },
  ];

  const mkString = (
    type: TubularStringType,
    topMD: number,
    bottomMD: number,
    holeDia: number | null,
    od: number | null,
    innerDia: number | null,
    weight: number | null,
    grade: string,
    material: string,
    connection: string,
    cement: CementInterval[],
  ): TubularString => ({
    id: mid(),
    sequence: seq++,
    type,
    topMD,
    bottomMD,
    holeDiameter: holeDia,
    cementIntervals: cement,
    joints: mkJoint(
      bottomMD - topMD,
      od,
      innerDia,
      weight,
      grade,
      material,
      connection,
    ),
    comment: "",
  });

  return {
    id: mid(),
    version: 1,
    isActive: true,
    dateActive: null,
    kopMD,
    landingMD,
    strings: [
      // Conductor: 508mm (20") in 546mm (21.5") hole, 0-46m (150ft), single cement
      // Depths in meters, diameters in mm, weight in kg/m
      mkString(
        "conductor",
        0,
        46,
        546,
        508,
        483,
        140,
        "K-55",
        "Carbon Steel",
        "STC",
        [mkCement(0, 46, "Class A", 15)],
      ),
      // ── CASING PROGRAM CORRECTED 2026-09-03 ────────────────────────────────────────────────
      // The program did not survive deepening the wells to a Delaware two-mile lateral. Three
      // defects, all of which put the schematic at odds with any drilling sequence:
      //
      //   1. SURFACE WAS A HARDCODED 488 m (1,600 ft) — a literal that did not scale with the
      //      well. Now derived from the formation column: below the Ogallala fresh-water sand
      //      (base 152 m) and ABOVE the Salado salt (top ~792 m), which is what a surface string
      //      is for. It also carries the BOP, so it must be competent rock.
      //   2. INTERMEDIATE RAN 0 → TD. That is ONE 19,400 ft hole section spanning 2,000 ft of
      //      mobile salt, the build AND the entire two-mile lateral. Not drillable as one
      //      section, and it left the timeline with no section boundaries to hang casing and
      //      cement milestones on.
      //   3. THERE WAS NO PRODUCTION CASING. The fourth string was TUBING — completion
      //      equipment, not a cemented string. The lateral had nothing run in it.
      //
      // The program is now the standard Permian four-string, shoes placed off the stratigraphy:
      //   conductor 20"    @   46 m (150 ft)
      //   surface   13-3/8" @  610 m (2,000 ft)   below fresh water, above salt
      //   intermediate 9-5/8" @ kopMD − 30 m      THROUGH the Salado, landing just above KOP
      //   production 5-1/2"  @ TD                 cemented through curve + lateral
      //
      // 13-3/8" replaces the old 12.5": 318 mm is not a standard casing OD, and 13-3/8" (340 mm)
      // is the Permian surface norm in a 17-1/2" hole.

      // Surface: 340mm (13-3/8") in 445mm (17.5") hole, 0-610m (2,000ft).
      // Cemented to surface — it isolates the fresh-water sand and is a regulatory requirement.
      mkString(
        "surface",
        0,
        610,
        445,
        340,
        315,
        101,
        "J-55",
        "Carbon Steel",
        "BTC",
        [mkCement(0, 305, "Class H", 10), mkCement(305, 610, "Class H", 12)],
      ),
      // Intermediate: 244mm (9-5/8") in 311mm (12.25") hole, 0 to just above KOP.
      // ITS JOB IS THE SALT. Cemented full-length back to surface: a salt section left uncased
      // creeps and washes out, and lead cement across it is what stops the hole closing on you.
      mkString(
        "intermediate",
        0,
        kopMD - 30,
        311,
        244,
        220,
        60,
        "N-80",
        "Carbon Steel",
        "BTC",
        [
          mkCement(0, (kopMD - 30) * 0.5, "Class H", 10),
          mkCement((kopMD - 30) * 0.5, kopMD - 30, "Class H", 12),
        ],
      ),
      // Production: 140mm (5-1/2") in 216mm (8.5") hole, 0 to TD — the curve and the lateral.
      // Cemented from TD back above the intermediate shoe so the completion has isolation to
      // stage against.
      mkString(
        "production",
        0,
        totalMD,
        216,
        140,
        124,
        31,
        "P-110",
        "Carbon Steel",
        "BTC",
        [
          mkCement(kopMD - 150, landingMD, "Class H", 12),
          mkCement(landingMD, totalMD, "Class H", 12),
        ],
      ),
      // Tubing: 73mm (2-7/8") production tubing, 0 to TD-30m.
      // CORRECTED 2026-09-03: this was 140 mm (5-1/2") OD in a 178 mm (7") HOLE —
      // the same OD as the production casing it runs inside, which cannot be run,
      // and a hole diameter for a string that is never in open hole. Tubing hangs
      // INSIDE the production casing, so it carries no holeDiameter and must be a
      // size smaller: 2-7/8" is the Permian norm inside 5-1/2".
      mkString(
        "tubing",
        0,
        totalMD - 30,
        null,
        73,
        62,
        10,
        "L-80",
        "Carbon Steel",
        "EUE",
        [],
      ),
    ],
    createdAt: now,
    modifiedAt: now,
    comment: "",
  };
};

/**
 * Generate hole sections from a tubular program.
 * Each casing string (excluding open hole and tubing) becomes a physical hole section.
 */
const generateHoleSections = (
  tubularProgram: TubularProgram,
): HoleSection[] => {
  const now = nowIso();
  const HOLE_TYPE_LABELS: Record<string, string> = {
    conductor: "Conductor",
    surface: "Surface",
    intermediate: "Intermediate",
    production: "Production",
    liner: "Liner",
  };

  let order = 1;
  return tubularProgram.strings
    .filter((s) => s.type !== "open_hole" && s.type !== "tubing")
    .map((s) => ({
      id: generateId(),
      name: HOLE_TYPE_LABELS[s.type] ?? s.type,
      topMD: s.topMD,
      bottomMD: s.bottomMD,
      holeSizeMM: s.holeDiameter ?? 311,
      casingStringId: s.id,
      parentSectionId: null,
      order: order++,
      createdAt: now,
      modifiedAt: now,
    }));
};

/** Generate ZIM report(s) from tubular program cement intervals.
 * When holeSections are provided, generates one ZIM report per physical section.
 * Otherwise generates a single report with all cement.
 */
const generateZIMReports = (
  tubularProgram: TubularProgram,
  holeSections?: HoleSection[],
): Report[] => {
  const makeSluryEntry = (ci: CementInterval) => ({
    id: generateId(),
    slurryType: "neat",
    slurryClass: ci.cementType || "Class H",
    manufacturer: randomChoice(["Halliburton", "Schlumberger", "BJ Services"]),
    productName: ci.cementType || "Class H",
    density: ci.density?.toString() ?? "16.4",
    thermalConductivity: "",
    compressiveStrength: randomInt(2000, 4000).toString(),
    additives: [],
    volumePumped: randomFloat(5, 30, 1).toString(),
    volumeReturned: randomFloat(0.5, 3, 1).toString(),
    topMD: ci.topMD.toString(),
    bottomMD: ci.bottomMD.toString(),
    topoffVolume: "",
    volumeUnit: "m3" as const,
    densityUnit: "ppg" as const,
    depthUnit: "m" as const,
    conductivityUnit: "W/mK" as const,
    compressiveStrengthUnit: "psi" as const,
    baseSlurryComment: "",
    pumpingDataComment: `${ci.cementType} placed from ${Math.round(ci.topMD)}m to ${Math.round(ci.bottomMD)}m`,
    topMDMeters: ci.topMD,
    bottomMDMeters: ci.bottomMD,
    densityKgM3: ci.density ? ci.density * 119.826 : null,
  });

  if (holeSections && holeSections.length > 0) {
    // One ZIM report per physical section that has a casing string with cement
    const reports: Report[] = [];
    for (const hs of holeSections.filter((s) => !s.parentSectionId)) {
      if (!hs.casingStringId) continue;
      const cs = tubularProgram.strings.find((s) => s.id === hs.casingStringId);
      if (!cs || cs.cementIntervals.length === 0) continue;

      reports.push({
        id: generateId(),
        reportType: "zonal-isolation",
        reportNumber: reports.length + 1,
        date: randomDate(90),
        data: {
          company: randomChoice(["Halliburton", "Schlumberger", "BJ Services"]),
          boreSection: hs.name,
          holeSectionId: hs.id,
          pumpMethod: "Pump & Plug",
          slurryEntries: cs.cementIntervals.map(makeSluryEntry),
        },
      });
    }
    return reports;
  }

  // Fallback: single report with all cement
  const slurryEntries: Record<string, unknown>[] = [];
  for (const s of tubularProgram.strings) {
    if (s.type === "open_hole" || s.type === "tubing") continue;
    for (const ci of s.cementIntervals) {
      slurryEntries.push(makeSluryEntry(ci));
    }
  }

  return [
    {
      id: generateId(),
      reportType: "zonal-isolation",
      reportNumber: 1,
      date: randomDate(90),
      data: {
        company: randomChoice(["Halliburton", "Schlumberger", "BJ Services"]),
        boreSection: "Full well",
        pumpMethod: "Pump & Plug",
        slurryEntries,
      },
    },
  ];
};

// ── Casing program test data (casing-report canon) ───────────────────────────
// The well-dashboard schematic derives from CASING REPORTS (useWellboreCasing), NOT the
// tubularPrograms above. Give each oilfield well a casing report so the schematic + the
// casing-tally UI have data to render. Depths are stored in FEET (store-as-entered + UOM —
// the program was authored in feet), ODs in inches; casingReportToSchematic reverses to SI.
const M_PER_FT_CSG = 0.3048;

/** One casing string carrying a single representative tally joint — the joint's `od` is what
 *  the schematic reads (getStringRepresentativeOD). Depths in ft, diameters in inches. */
const makeCasingString = (
  stringName: string,
  stringType: string,
  odIn: number,
  holeSizeIn: number,
  topFt: number,
  shoeFt: number,
  weight: number,
  grade: string,
): CasingString => {
  const joint: CasingTallyItem = {
    id: generateId(),
    sequence: 1,
    type: "Joint",
    od: String(odIn),
    odUnit: "in",
    innerId: "",
    weight: String(weight),
    weightUnit: "lb/ft",
    grade,
    connection: "",
    length: shoeFt.toFixed(1),
    lengthUnit: "ft",
    jointNumber: "1",
    description: "",
  };
  return {
    id: generateId(),
    stringName,
    stringType,
    casingSize: String(odIn),
    casingSizeUnit: "in",
    nominalWeight: String(weight),
    nominalWeightUnit: "lb/ft",
    grade,
    connection: "",
    material: "",
    innerId: "",
    drift: "",
    holeSize: holeSizeIn > 0 ? String(holeSizeIn) : "",
    holeSizeUnit: "in",
    topDepth: topFt.toFixed(1),
    topDepthUnit: "ft",
    shoeDepthActual: shoeFt.toFixed(1),
    shoeDepthActualUnit: "ft",
    tally: [joint],
    runningParams: {
      handlingMethod: "",
      floatation: "",
      fillSchedule: "",
      landingWeight: "",
      slipsWeight: "",
      runningSpeed: "",
      runningSpeedUnit: "",
      reciprocation: "",
      circulation: "",
      makeupTorque: "",
      maxOverpull: "",
    },
    inspection: {
      dateTime: "",
      company: "",
      inspectors: "",
      standard: "",
      notes: "",
      tally: [],
    },
  };
};

// Nominal sizes: the tubular program stores SI (mm OD, mm hole), the casing report stores the
// oilfield NOMINAL inch sizes an engineer reads off a schematic. A bare mm/25.4 gives 13.386"
// for a 13-3/8" string, so the nominal is looked up and only falls back to arithmetic for a
// size not in the table.
const NOMINAL_OD_BY_MM: Record<number, { inches: number; label: string }> = {
  508: { inches: 20, label: '20"' },
  340: { inches: 13.375, label: '13-3/8"' },
  244: { inches: 9.625, label: '9-5/8"' },
  140: { inches: 5.5, label: '5-1/2"' },
  73: { inches: 2.875, label: '2-7/8"' },
};
const NOMINAL_HOLE_IN_BY_MM: Record<number, number> = {
  546: 21.5,
  445: 17.5,
  311: 12.25,
  216: 8.5,
  178: 7,
};
/** kg/m → lb/ft. The program's kg/m values are the SI equivalents of real API weights
 *  (101 kg/m ≙ 68 lb/ft for 13-3/8", 60 ≙ 40 for 9-5/8"), so this round-trips to nominal. */
const LB_PER_FT_PER_KG_PER_M = 0.6719689;

/** The casing REPORT — DERIVED FROM THE TUBULAR PROGRAM, not hand-written beside it.
 *
 *  DUPLICATE-REPRESENTATION FIX (2026-09-03). This function used to carry its OWN copy of the
 *  casing program — 13-3/8" conductor @ 500 ft, 9-5/8" surface @ 1,200 ft, 7" production, 4"
 *  tubing @ KOP — hardcoded independently of `generateOilfieldTubularProgram`. The two drifted
 *  the moment the program was corrected for the Delaware two-mile geometry: the schematic
 *  renders from THIS report (useWellboreCasing → casingStringsToSchematic), so the app was
 *  showing a 9-5/8" surface string at 1,200 ft while the tubular program said 13-3/8" at
 *  2,000 ft. Same defect class as the depth/stratigraphy drift above: two copies of one fact.
 *
 *  Now there is one source. Change a shoe in the program and the report, the schematic and the
 *  time log all move with it. */
const generateCasingReport = (program: TubularProgram): Report => {
  const casingStrings: CasingString[] = program.strings.map((str) => {
    const odMm = str.joints[0]?.od ?? 0;
    const nominal = NOMINAL_OD_BY_MM[odMm] ?? {
      inches: Math.round((odMm / 25.4) * 1000) / 1000,
      label: `${(odMm / 25.4).toFixed(3)}"`,
    };
    const holeMm = str.holeDiameter ?? 0;
    const holeIn =
      NOMINAL_HOLE_IN_BY_MM[holeMm] ?? Math.round((holeMm / 25.4) * 100) / 100;
    const kgPerM = str.joints[0]?.weight ?? 0;
    const lbPerFt = Math.round(kgPerM * LB_PER_FT_PER_KG_PER_M * 10) / 10;
    const stringType = str.type.charAt(0).toUpperCase() + str.type.slice(1);
    return makeCasingString(
      `${nominal.label} ${stringType}`,
      stringType,
      nominal.inches,
      holeIn,
      str.topMD / M_PER_FT_CSG,
      str.bottomMD / M_PER_FT_CSG,
      lbPerFt,
      str.joints[0]?.grade ?? "",
    );
  });
  const data: CasingReportData = { casingStrings };
  return {
    id: generateId(),
    reportType: "casing" as const,
    reportNumber: 1,
    date: randomDate(20),
    data,
  } as unknown as Report;
};

/**
 * Generate oilfield test data: 10 horizontal wells on a pad.
 * For smoke-testing horizontal trajectory rendering, oilfield casing programs,
 * and Permian-style geology.
 */
/** The fixed clock a deterministic build stamps. Arbitrary but STABLE — it is an audit stamp on
 *  synthetic data, not a fact about when anything happened. The drilling dates come from the 2018
 *  campaign anchor, not from here. */
const DETERMINISTIC_BUILD_STAMP = "2026-09-03T00:00:00.000Z";

export const generateOilfieldTestData = (): Borefield => {
  // DETERMINISTIC BY DEFAULT. This pad is built into a database and served read-only, so its ids
  // are primary keys — they cannot change between calls. Restored in `finally` so a throw cannot
  // leave the mode latched on for the geothermal generators, which still roll random ids.
  const prevIds = deterministicIds;
  const prevNow = deterministicNow;
  deterministicIds = 0;
  deterministicDraw = 0;
  deterministicNow = DETERMINISTIC_BUILD_STAMP;
  try {
    return buildOilfieldTestData();
  } finally {
    deterministicIds = prevIds;
    deterministicNow = prevNow;
  }
};

const buildOilfieldTestData = (): Borefield => {
  const rig = generateRig(0);
  const bores: Bore[] = [];
  // S6 — what the stimulation pass needs from each well, collected during the drilling
  // loop because the frac campaign cannot be scheduled until the LAST well is down: it
  // starts after the PAD finishes drilling, not after each well does.
  const stimSeeds: {
    boreId: string;
    boreName: string;
    landingMD: number;
    totalMD: number;
    drillEndIso: string;
  }[] = [];
  // HOISTED (2026-09-03): the pad id used to be minted in the return literal, but a
  // TimeEntry is keyed on padId/wellboreId/jobId, so the time log needs it while the
  // bores are being built.
  const padId = generateId();
  // One rig walks the pad SEQUENTIALLY, so exactly one well is drilling at any
  // instant — index 8, the one whose boreStatus is "Drilling". Every other well's
  // time log is offset from that single rig clock (see oilfieldTimeLog anchoring).
  // THE PAD IS HISTORICAL (PO 2026-09-03, DECISIONS.md "The Permian demo pad is HISTORICAL"):
  // all ten wells were drilled in 2018 by one rig walking the pad, and the live job today is a
  // workover, not drilling. `DRILLING_INDEX` used to name well 9 as the one currently on the rig
  // and to offset every other well from `Date.now()`; both are gone. `nowMs` is still passed to
  // `generateOilfieldTimeLog` for its `createdAt` stamps, which are genuinely "now".
  // The audit clock. In deterministic mode this is the fixed build stamp, so `createdAt` /
  // `modifiedAt` on every generated time entry reproduce too — the last thing that varied between
  // builds. Placement no longer depends on it: each well's position comes from `originMs`.
  const nowMs = Date.parse(nowIso());

  /** Move-in for the FIRST well, at local midnight so a rig day boundary lands where a daily
   *  report puts it. Backdated so completion, production and two rounds of workovers fit between
   *  the drilling campaign and today. */
  // LOCAL midnight in the PAD'S OWN ZONE, which the build pins (`build_demo_pad.ts` refuses to run
  // unless TZ is America/Chicago — Loving County). Local, not UTC: report days are local by repo
  // convention (`lib/date.ts`), and `Date.UTC` midnight is the previous EVENING in Texas, so a UTC
  // anchor spudded the pad on 2018-03-04. Determinism comes from pinning the zone, not from
  // rendering a different calendar than the rest of the app.
  const PAD_SPUD_2018 = new Date(2018, 2, 5, 0, 0, 0, 0).getTime();
  /** Hours between one well's rig release and the next well's move-in. */
  const RIG_MOVE_H = 6;

  // ── CAMPAIGN PRE-PASS ──────────────────────────────────────────────────────────────────────
  // Well N's move-in is the sum of every PRIOR well's duration, and a well's duration comes from
  // its own geometry via the casing program. So the geometries and the schedule must both be known
  // before any bore is built — hence a pass before the loop rather than inside it.
  //
  // Geometry is SEEDED, not rolled. `randomInt` here meant every reload produced a different
  // corpus: you could not demo the same number twice, screenshot it, or write a test that asserts
  // one. The bands are unchanged — a Delaware two-mile Wolfcamp, documented at the loop below.
  const padGeometry = Array.from({ length: 10 }, (_, i) => {
    const kopMD = seededInt(2987, 3140, "permian-kop", i);
    const buildLength = seededInt(335, 427, "permian-build", i);
    const landingMD = kopMD + buildLength;
    const lateralLength = seededInt(2896, 3200, "permian-lat", i);
    return {
      kopMD,
      buildLength,
      landingMD,
      lateralLength,
      totalMD: landingMD + lateralLength,
    };
  });

  /** Shoe depths + trajectory for well `i`, read off the casing program that well will actually
   *  get — never re-derived, so the schedule and the schematic cannot disagree about the shoes. */
  const wellGeometryFor = (i: number): OilfieldWellGeometry => {
    const g = padGeometry[i]!;
    const tp = generateOilfieldTubularProgram(g.totalMD, g.kopMD, g.landingMD);
    return {
      conductorShoeMD: tp.strings[0]?.bottomMD ?? 46,
      surfaceShoeMD: tp.strings[1]?.bottomMD ?? 610,
      intermediateShoeMD: tp.strings[2]?.bottomMD ?? g.kopMD - 30,
      kopMD: g.kopMD,
      landingMD: g.landingMD,
      totalMD: g.totalMD,
    };
  };

  /** Move-in instant for each well — one rig, in sequence, with a move between. */
  const padOriginMs: number[] = [];
  {
    let cursor = PAD_SPUD_2018;
    for (let i = 0; i < 10; i++) {
      padOriginMs.push(cursor);
      const { releaseH } = buildSequence(
        wellGeometryFor(i),
        i === 0 ? "the yard" : `PH-${i}H`,
      );
      cursor += (releaseH + RIG_MOVE_H) * 3_600_000;
    }
  }

  // Permian Basin (Midland, TX) surface layout — TWO ROWS OF 5 wellheads, each row a clean straight
  // line so it forms one below-grade cellar (lasso a row → a cellar). ~15 m between wellheads along a
  // row (E-W via longitude) and ~40 m between the two rows (N-S via latitude) — realistic multi-well
  // pad geometry, and the rows are well separated so one lassoes without catching the other. (Prior:
  // START_LAT/START_LON put this "Permian" pad in Detroit — the geothermal-heritage default.)
  // LOVING COUNTY, TEXAS — the Delaware Basin, moved here 2026-09-03 to match the stratigraphic
  // column above, which was always Delaware (Salado · Rustler · Bone Spring · Wolfcamp) while the
  // pad sat in Midland County. Different basin, different column. Loving is also where the deepest
  // Wolfcamp laterals actually are, which is what the geometry below now models.
  const PAD_LAT = 31.85;
  const PAD_LON = -103.65;
  const ROW_SPACING = 0.00036; // ~40 m N-S between the two rows
  const COL_SPACING = 0.00016; // ~15 m E-W between wellheads within a row

  for (let i = 0; i < 10; i++) {
    // All depths in metres (SI) — a modern Delaware Basin two-mile Wolfcamp lateral.
    //
    // DEEPENED 2026-09-03. It was 1463-1585 m TVD (4,800-5,200 ft) with a 2,896-3,200 m
    // (9,500-10,500 ft) total MD — a one-mile lateral into a Wolfcamp sitting 6,000 ft shallower
    // than Wolfcamp actually is. A 2026 Delaware well is ~11,000 ft TVD with a 10,000 ft lateral,
    // and the drilling clock that goes with it is 18-30 days, not 10-16.
    //
    //   TVD      3292-3475 m  = 10,800-11,400 ft   → lands in Wolfcamp A/B per the column above
    //   KOP      2987-3140 m  =  9,800-10,300 ft
    //   Build     335-427 m   =  1,100-1,400 ft    → 90° at ~8-10°/100 ft
    //   Lateral  2896-3200 m  =  9,500-10,500 ft   → two-mile
    //   Total MD             ~= 20,500-22,200 ft
    // Seeded in the campaign pre-pass above; the bands are documented in the comment block above.
    // The trajectory reads kop/landing/total, so `buildLength` and `lateralLength` are not
    // destructured here — they are inputs to those three, not separate stored values.
    const { kopMD, landingMD, totalMD } = padGeometry[i]!;

    // TVD IS DELIBERATELY NOT STORED HERE. It falls out of the trajectory —
    // `generateHorizontalSurveyPoints` runs minimum-curvature and every station carries its own
    // computed TVD — so the survey is the single source of truth for it.
    //
    // An earlier version of this block asserted `trueVerticalDepth` on the bore literal. TWO
    // things were wrong with that and both are worth recording:
    //   1. NO SUCH FIELD EXISTS. The real one is `DeviationSummary.maxTrueVerticalDepth`, which is
    //      written AFTER a trajectory calculation, not hand-set by a generator. `const bore = {…}`
    //      is an untyped object literal, so tsc inferred the shape and accepted the invented field
    //      in silence — the same class as the `AFELines.Comment` incident CLAUDE.md documents.
    //   2. Even with the right name it would be a SECOND representation of a number the survey
    //      already computes, which is how two copies drift apart.
    //
    // For the record, the arc geometry: a constant-build-rate 0°→90° turn gains vertical equal to
    // its radius, R = arcLength/(π/2), so landing TVD ≈ KOP + 2·buildLength/π ≈ 10,600–11,200 ft
    // here — which lands in Wolfcamp A/B per the column above. That is a check, not a stored value.
    const targetAzi = i * 36 + randomFloat(-5, 5, 1); // Fanned 36° apart

    const lat = PAD_LAT + Math.floor(i / 5) * ROW_SPACING;
    const lon = PAD_LON + (i % 5) * COL_SPACING;
    const latStr = lat.toFixed(6);
    const lonStr = lon.toFixed(6);
    const boreName = `PH-${i + 1}H`;

    const mwdSurveyId = generateId();
    const plannedSurveyId = generateId();

    // Determine bore status
    let boreStatus: string;
    // Every well on this pad was DRILLED in 2018 (PO 2026-09-03). Wells 7 and 8 were later
    // plugged; the rest produce. Nothing is mid-drilling and nothing is un-spudded, because the
    // campaign finished eight years ago — the live job on this pad is a workover (not yet built).
    if (i === 6 || i === 7)
      boreStatus = "Abandoned"; // P&A candidates
    else boreStatus = "Completed";

    // Generate tubular program, hole sections, and ZIM reports
    const tubProg = generateOilfieldTubularProgram(totalMD, kopMD, landingMD);
    const holeSections = generateHoleSections(tubProg);
    const zimReports = generateZIMReports(tubProg, holeSections);

    // ── TIME LOG (added 2026-09-03) ──────────────────────────────────────────
    // The generated pad had a schematic and no timeline: nothing wrote Job.timeLog,
    // and useTimeline REFUSES to seed a real job-bound log (Abs-7 — no fabricated
    // data on a real key), so every well opened onto an empty time log. The job's
    // creator has to supply it, which for demo data is this generator.
    //
    // The sequence is DERIVED FROM THE CASING PROGRAM above — each cemented string
    // is a drill → circulate → trip → run casing → cement → WOC → integrity-test
    // cycle — so the timeline and the schematic cannot disagree about where the
    // shoes are.
    const boreId = generateId();
    const jobId = generateId();
    // Every drilling job on this pad finished in 2018, so every time log is a completed one.
    const phase: OilfieldWellPhase = "completed";
    // Shoe depths MUST come from the program that was just generated, not be re-derived here —
    // two copies of a shoe depth is how they drift apart. `wellGeometryFor(i)` builds the same
    // program from the same inputs, which is what let the campaign pre-pass schedule this well
    // before the loop reached it.
    const wellGeometry: OilfieldWellGeometry = {
      conductorShoeMD: tubProg.strings[0]?.bottomMD ?? 46,
      surfaceShoeMD: tubProg.strings[1]?.bottomMD ?? 610,
      intermediateShoeMD: tubProg.strings[2]?.bottomMD ?? kopMD - 30,
      kopMD,
      landingMD,
      totalMD,
    };
    const previousWell = i === 0 ? "the yard" : `PH-${i}H`;
    const originMs = padOriginMs[i]!;
    const timeLogResult = generateOilfieldTimeLog({
      ids: { padId, wellboreId: boreId, jobId },
      geometry: wellGeometry,
      phase,
      nowMs,
      // Placement comes from `originMs`, so the offset is not consulted. Kept at 0 rather than
      // removed: the field is required, and a stale non-zero value would read as meaningful.
      campaignOffset: 0,
      previousWell,
      originMs,
    });

    // ── DAILY DRILLING COSTS ────────────────────────────────────────────────
    // One cost-log report per rig day, every line justified by what the rig was doing that day.
    // The day view reads the SAME sequence the time log was built from, so the cost log and the
    // timeline cannot disagree about which day ran casing.
    // ONE day program per well, shared by every per-day family. The cost log and the mud log must
    // agree about which day the bit made 3,100 m; generating a program each would let them drift.
    const dayProgram = demoWellProgram(wellGeometry, previousWell, originMs, i);
    const costLogReports = generateDemoCostLogReports(dayProgram, boreName);
    const mudCheckReports = generateDailyMudCheckReports(dayProgram, boreName);

    const bore = {
      id: boreId,
      boreName,
      boreType: "Oil Producer" as const,
      boreStatus,
      latitude: latStr,
      longitude: lonStr,
      elevation: randomFloat(975, 1036, 1).toString(), // ~3200-3400 ft → meters
      designDepth: totalMD.toString(),
      actualDepth: boreStatus === "Planned" ? "" : totalMD.toString(),
      depthUnit: "m", // SI — all depths stored in meters
      workingFluid: "water",
      comment: `Horizontal well targeting Wolfcamp A — azimuth ${Math.round(targetAzi)}°`,
      activeSurveyId: boreStatus === "Planned" ? plannedSurveyId : mwdSurveyId,
      surveys: [
        {
          id: plannedSurveyId,
          name: `${boreName} Planned`,
          type: "planned" as const,
          date: randomDate(180),
          latitude: latStr,
          longitude: lonStr,
          points: generateHorizontalSurveyPoints(
            kopMD,
            landingMD,
            totalMD,
            targetAzi,
          ),
          createdAt: nowIso(),
        },
        {
          id: mwdSurveyId,
          name: `${boreName} MWD`,
          type: "mwd" as const,
          date: randomDate(90),
          latitude: latStr,
          longitude: lonStr,
          points: generateHorizontalSurveyPoints(
            kopMD,
            landingMD,
            totalMD,
            targetAzi,
          ),
          createdAt: nowIso(),
        },
      ],
      tubularPrograms: [tubProg],
      holeSections,
      jobs: [
        {
          id: jobId,
          jobType: "construction",
          jobNumber: `Drilling - ${boreName}`,
          status:
            phase === "completed"
              ? "Completed"
              : phase === "planned"
                ? "Planned"
                : "In Progress",
          // DATES ARE DERIVED FROM THE TIME LOG, not rolled independently. The
          // start date used to be `now − random(30,180) days`, unrelated to any
          // other date on the well — a job whose header said one thing and whose
          // (absent) time log would have said another. Now the log is the single
          // source and the header reads off it.
          startDate: timeLogResult.startDate,
          spudDate: timeLogResult.spudDate,
          ...(timeLogResult.endDate ? { endDate: timeLogResult.endDate } : {}),
          timeLog: timeLogResult.timeLog,
          rigId: rig.id,
          reports: [
            generateOilfieldFormationsReport(totalMD, dayProgram),
            generateCasingReport(tubProg),
            ...zimReports,
            ...costLogReports,
            ...mudCheckReports,
          ],
        },
      ],
      createdAt: nowIso(),
      modifiedAt: nowIso(),
    } as unknown as Bore;

    // Abandoned bores get a P&A job
    if (boreStatus === "Abandoned") {
      (bore as unknown as Record<string, unknown[]>).jobs = [
        ...((bore as unknown as Record<string, unknown[]>).jobs ?? []),
        {
          id: generateId(),
          jobType: "abandon",
          jobNumber: `P&A - ${boreName}`,
          status: i === 6 ? "Completed" : "In Progress",
          // Plugged RECENTLY, not in 2018: these wells produced for years before reaching their
          // economic limit, which is what gives the abandonment AFE a plausible home. Off the
          // deterministic clock so a rebuild reproduces.
          startDate: new Date(
            Date.parse(nowIso()) - randomInt(10, 30) * 86400000,
          ).toISOString(),
          reports: [],
        },
      ];
    }

    stimSeeds.push({
      boreId,
      boreName,
      landingMD,
      totalMD,
      drillEndIso: timeLogResult.endDate ?? timeLogResult.startDate,
    });

    bores.push(bore);
  }

  // ── 11th well: a real near-U-turn HORSESHOE (U-Michigan EGX "Ann Arbor" survey) ──
  // The 10 wells above are gentle build-and-hold curves; this one turns ~110° in
  // azimuth (350°→240°) at horizontal, so it exercises the subway schematic's
  // fold-back rendering that the gentle wells never trigger. The survey is the real
  // welleng-computed trajectory (SI metres), embedded verbatim (cloned per call so
  // the shared constant is never mutated). Placed in its own third row.
  {
    const hsTotalMD = 3658; // ~12,000 ft MD
    const hsKopMD = 1250; // ~4,100 ft (kickoff)
    const hsLandingMD = 1536; // ~5,040 ft (heel)
    const lat = PAD_LAT + 2 * ROW_SPACING; // a third row, clear of the 2×5 grid
    const lon = PAD_LON + 2 * COL_SPACING;
    const latStr = lat.toFixed(6);
    const lonStr = lon.toFixed(6);
    const boreName = "PH-11H";
    const plannedSurveyId = generateId();
    const tubProg = generateOilfieldTubularProgram(
      hsTotalMD,
      hsKopMD,
      hsLandingMD,
    );
    const holeSections = generateHoleSections(tubProg);
    const zimReports = generateZIMReports(tubProg, holeSections);

    // The horseshoe is a COMPLETED well drilled BEFORE the ten-well program, so it comes off the
    // same rig clock one cycle ahead of PH-1H. With the pad anchored to a fixed 2018 date rather
    // than to `now`, "one cycle ahead" is expressed directly: back up from the pad spud by this
    // well's own duration plus a rig move.
    const hsBoreId = generateId();
    const hsJobId = generateId();
    const hsGeometry: OilfieldWellGeometry = {
      conductorShoeMD: tubProg.strings[0]?.bottomMD ?? 46,
      surfaceShoeMD: tubProg.strings[1]?.bottomMD ?? 610,
      intermediateShoeMD: tubProg.strings[2]?.bottomMD ?? hsKopMD - 30,
      kopMD: hsKopMD,
      landingMD: hsLandingMD,
      totalMD: hsTotalMD,
    };
    const hsOriginMs =
      PAD_SPUD_2018 -
      (buildSequence(hsGeometry, "the yard").releaseH + RIG_MOVE_H) * 3_600_000;
    const hsTimeLog = generateOilfieldTimeLog({
      ids: { padId, wellboreId: hsBoreId, jobId: hsJobId },
      geometry: hsGeometry,
      phase: "completed",
      nowMs,
      campaignOffset: 0,
      previousWell: "the yard",
      originMs: hsOriginMs,
    });
    const hsDayProgram = demoWellProgram(hsGeometry, "the yard", hsOriginMs, 0);
    const hsCostLogReports = generateDemoCostLogReports(hsDayProgram, boreName);
    const hsMudCheckReports = generateDailyMudCheckReports(
      hsDayProgram,
      boreName,
    );

    const horseshoe = {
      id: hsBoreId,
      boreName,
      boreType: "Oil Producer" as const,
      boreStatus: "Completed",
      latitude: latStr,
      longitude: lonStr,
      elevation: randomFloat(975, 1036, 1).toString(),
      designDepth: hsTotalMD.toString(),
      actualDepth: hsTotalMD.toString(),
      depthUnit: "m", // SI — all depths stored in meters
      workingFluid: "water",
      comment:
        "Horseshoe / near-U-turn (U-Michigan EGX 'Ann Arbor' survey) — exercises the subway fold-back",
      activeSurveyId: plannedSurveyId,
      surveys: [
        {
          id: plannedSurveyId,
          name: `${boreName} Planned`,
          type: "planned" as const,
          date: randomDate(180),
          latitude: latStr,
          longitude: lonStr,
          // clone so the shared module constant is never mutated by consumers
          points: ANN_ARBOR_HORSESHOE_SURVEY_M.map((p) => ({ ...p })),
          createdAt: nowIso(),
        },
      ],
      tubularPrograms: [tubProg],
      holeSections,
      jobs: [
        {
          id: hsJobId,
          jobType: "construction",
          jobNumber: `Drilling - ${boreName}`,
          status: "Completed",
          startDate: hsTimeLog.startDate,
          spudDate: hsTimeLog.spudDate,
          ...(hsTimeLog.endDate ? { endDate: hsTimeLog.endDate } : {}),
          timeLog: hsTimeLog.timeLog,
          rigId: rig.id,
          reports: [
            ...hsCostLogReports,
            ...hsMudCheckReports,
            generateOilfieldFormationsReport(hsTotalMD, hsDayProgram),
            generateCasingReport(tubProg),
            ...zimReports,
          ],
        },
      ],
      createdAt: nowIso(),
      modifiedAt: nowIso(),
    } as unknown as Bore;

    bores.push(horseshoe);
  }

  // ── S6: the STIMULATION campaign (PLAN_permian_demo_corpus.md) ──
  //
  // A SECOND PASS, deliberately. The frac campaign starts after the PAD is down, not
  // after each well is, so it cannot be scheduled inside the drilling loop — the last
  // well's release date is not known until the loop ends.
  //
  // The horseshoe (PH-11H) is NOT stimulated: it exists to exercise the schematic's
  // fold-back rendering, and giving it a frac job would put an eleventh well into a
  // zipper layout authored for ten.
  //
  // NOTHING HERE IS DERIVED. Stage counts, durations and the zipper cadence are
  // authored constants in demoStimulation.ts — see its header and Charter Abs-2.
  const padStimulationEntries: StimulationEntry[] = [];
  {
    const padDrillingEndIso = stimSeeds.reduce(
      (latest, s) => (s.drillEndIso > latest ? s.drillEndIso : latest),
      stimSeeds[0]?.drillEndIso ?? nowIso(),
    );
    for (let w = 0; w < stimSeeds.length; w++) {
      const seed = stimSeeds[w];
      if (!seed) continue;
      const bore = bores.find((b) => b.id === seed.boreId);
      if (!bore) continue;
      const { job, data, padEntries } = buildDemoStimulation({
        padId,
        wellboreId: seed.boreId,
        jobId: generateId(),
        wellIndex: w,
        boreName: seed.boreName,
        landingMD: seed.landingMD,
        totalMD: seed.totalMD,
        padDrillingEndIso,
        nowIso: nowIso(),
      });
      // The stream rides the JOB (beside timeLog, which the drilling job carries the
      // same way), so it reaches the front end through the well bundle and the
      // DATABASE rather than localStorage (PO 2026-09-04).
      const jobWithStream = { ...job, stimulation: data };
      const holder = bore as unknown as Record<string, unknown[]>;
      holder.jobs = [...(holder.jobs ?? []), jobWithStream];
      // The shared services every well takes its turn on — wireline, plugs, coil,
      // flowback, spread rig-up, safety meetings — are PAD-scoped, not well-scoped.
      // They render on the COMMON band, which reads one pad stream; putting them on
      // the well made 39.7% of the first version's entries render nowhere at all.
      padStimulationEntries.push(...padEntries);
    }
  }

  return {
    id: padId,
    // PAD-SCOPED stimulation stream (S6). Shared services — wireline, plugs, coil,
    // flowback, spread rig-up/down, safety meetings — belong to the PAD, not a well:
    // one spread and one wireline unit serve every well in a zipper group. Each entry
    // carries `targetWellboreId` so the common band reads "wireline on Well B".
    // Consumed via the pad-scoped store (STIMULATION_PAD_STORAGE_PREFIX + padId).
    stimulationPad: padStimulationEntries,
    borefieldName: "Permian Horizontal Pad",
    // Not "BIOT-k Engineering" — a leftover of the extraction from the sibling app, and visible on
    // the pad header of the demo an operator is meant to be looking at.
    ownerName: "OXBO Energy",
    operatorName: "Permian Operations LLC",
    systemType: "Oil & Gas",
    status: "Operational" as const,
    address: {
      street: "1 Pad Site Road",
      city: "",
      stateProvince: "TX",
      // LOVING COUNTY, not Midland (2026-09-03). PAD_LAT/PAD_LON moved to the
      // Delaware Basin to match the stratigraphic column, but this address block
      // was left behind pointing at Midland — 79706 is a Midland zip and 48329 is
      // the Midland County FIPS. Mentone 79754 / FIPS 48301 is Loving County.
      // The old fipsCode "4830" was a truncated 4-digit value; a county FIPS is 5.
      postalCode: "79754",
      countyParish: "Loving",
    },
    geodetic: {
      fipsCode: "48301",
      declination: "7.5",
      datum: "NAD83",
      elevationDatum: "NAVD88",
    },
    bores,
    rigs: [rig],
    defaultRigId: rig.id,
    actualBoreCount: bores.length, // 10 gentle wells + the PH-11H horseshoe
    isSingleBore: false,
    createdAt: nowIso(),
    modifiedAt: nowIso(),
  } as Borefield;
};

export default {
  generateSmallTestData,
  generateOilfieldTestData,
};
